import { z } from 'zod';

import { CorporateGroupViewSchema } from '../traveler/corporate_group_view';

// Chat integrations of a corporate group — Microsoft Teams and Slack. Two
// contracts, provider-neutral: each connector's `/internal` connect surface (read
// by kanban-api) and kanban-api's staff-only `/admin/integrations` surface (read
// by the operator console). An "account" is the client's side of the link: a
// Microsoft 365 tenant or a Slack workspace.

export const ChatProviderSchema = z.enum(['teams', 'slack']);
export type ChatProvider = z.infer<typeof ChatProviderSchema>;

// ── connector /internal ──────────────────────────────────────────────────────

export const ChatConnectLinkStatusSchema = z.enum([
  'pending',
  'review',
  'connected',
  'revoked',
  'rejected',
]);
export type ChatConnectLinkStatus = z.infer<typeof ChatConnectLinkStatusSchema>;

/** The client account that authorized: a Microsoft 365 tenant or a Slack workspace. */
export const ChatAccountSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  /** Tenant verified domains (Teams) or the installer's e-mail domain (Slack). */
  domains: z.array(z.string()),
  /** Who authorized, when the provider says (Slack: the installer's e-mail). */
  authorizedBy: z.string().optional(),
});
export type ChatAccount = z.infer<typeof ChatAccountSchema>;

/** A connect link VOLL staff sent to a client's IT. Never carries the token. */
export const ChatConnectLinkViewSchema = z.object({
  corporateGroupId: z.string(),
  corporateGroupName: z.string(),
  status: ChatConnectLinkStatusSchema,
  createdBy: z.string(),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  /** A pending link past `expiresAt`: the landing page refuses it. */
  expired: z.boolean(),
  account: ChatAccountSchema.optional(),
  authorizedAt: z.iso.datetime().optional(),
});
export type ChatConnectLinkView = z.infer<typeof ChatConnectLinkViewSchema>;

/** A live account (tenant or workspace) and the groups it is linked to (`[]` = no company yet). */
export const ChatInstallationViewSchema = z.object({
  account: ChatAccountSchema,
  corporateGroupIds: z.array(z.string()),
  installedAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type ChatInstallationView = z.infer<typeof ChatInstallationViewSchema>;

export const ChatConnectionsResponseSchema = z.object({
  /** Accounts linked to at least one group. */
  installations: z.array(ChatInstallationViewSchema),
  /** Pending and in-review links, newest first. */
  links: z.array(ChatConnectLinkViewSchema),
  /** Accounts installed with no company (Slack's public install link); `[]` for Teams. */
  unlinked: z.array(ChatInstallationViewSchema),
});
export type ChatConnectionsResponse = z.infer<typeof ChatConnectionsResponseSchema>;

const GroupIdSchema = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);

export const ChatConnectLinkCreateRequestSchema = z.object({
  corporateGroupId: GroupIdSchema,
  corporateGroupName: z.string().trim().min(1).max(200),
  /** The group's traveler e-mail domains: the authorizing account must share one. */
  expectedDomains: z
    .array(
      z
        .string()
        .max(253)
        .regex(/^[a-z0-9.-]+\.[a-z]{2,}$/i),
    )
    .max(50),
  createdBy: z.string().trim().min(1).max(320),
});
export type ChatConnectLinkCreateRequest = z.infer<typeof ChatConnectLinkCreateRequestSchema>;

export const ChatConnectLinkCreateResponseSchema = z.object({
  /** Shown once: the token is not stored, a lost link is regenerated. */
  url: z.url(),
  link: ChatConnectLinkViewSchema,
});
export type ChatConnectLinkCreateResponse = z.infer<typeof ChatConnectLinkCreateResponseSchema>;

/** Links an already-installed account (no company yet) to a group — Slack's public installs. */
export const ChatAccountLinkRequestSchema = z.object({ corporateGroupId: GroupIdSchema });
export type ChatAccountLinkRequest = z.infer<typeof ChatAccountLinkRequestSchema>;

// ── kanban-api /admin/integrations ───────────────────────────────────────────

/** Where a group stands with one chat tool. `pending` covers expired links (`link.expired`). */
export const ChatConnectionStateSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('none') }),
  /** The connector is off in this environment (kanban has no URL for it, or it answered 503). */
  z.object({ status: z.literal('unavailable') }),
  z.object({ status: z.literal('pending'), link: ChatConnectLinkViewSchema }),
  z.object({ status: z.literal('review'), link: ChatConnectLinkViewSchema }),
  z.object({ status: z.literal('connected'), installation: ChatInstallationViewSchema }),
]);
export type ChatConnectionState = z.infer<typeof ChatConnectionStateSchema>;

export const IntegrationCompanySchema = CorporateGroupViewSchema.extend({
  teams: ChatConnectionStateSchema,
  slack: ChatConnectionStateSchema,
});
export type IntegrationCompany = z.infer<typeof IntegrationCompanySchema>;

export const IntegrationCompaniesResponseSchema = z.object({
  data: z.array(IntegrationCompanySchema),
});
export type IntegrationCompaniesResponse = z.infer<typeof IntegrationCompaniesResponseSchema>;

/** Pendências: open links of every tool, plus Slack workspaces with no company. */
export const IntegrationPendingResponseSchema = z.object({
  links: z.array(ChatConnectLinkViewSchema.extend({ provider: ChatProviderSchema })),
  unlinked: z.array(ChatInstallationViewSchema.extend({ provider: ChatProviderSchema })),
});
export type IntegrationPendingResponse = z.infer<typeof IntegrationPendingResponseSchema>;
